import React, { useState, useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import styles from './TimetableConfiguration.module.css';

const TimetableConfiguration = ({ configuration, onSave }) => {
  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: configuration || {
      academicYear: new Date().getFullYear().toString(),
      term: 'Term 1',
      periodsPerDay: 8,
      daysPerWeek: 6,
      periodDuration: 40,
      schoolStartTime: '08:00',
      schoolEndTime: '15:00',
       effectiveFrom: new Date().toISOString().split('T')[0], 
      breaks: []
    }
  });

  const { fields: breakFields, append: appendBreak, remove: removeBreak } = useFieldArray({
    control,
    name: 'breaks'
  });

  const periodsPerDay = watch('periodsPerDay');
  const daysPerWeek = watch('daysPerWeek');

  const onSubmit = (data) => {
    onSave(data);
  };

  const addBreak = () => {
    appendBreak({
      breakName: `Break ${breakFields.length + 1}`,
      afterPeriod: 0,
      duration: 15,
      startTime: '10:00',
      endTime: '10:15'
    });
  };

  return (
    <div className={styles.configContainer}>
      <h3>Timetable Configuration</h3>
      
      <form onSubmit={handleSubmit(onSubmit)} className={styles.configForm}>
        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <label>Academic Year *</label>
            <Controller
              name="academicYear"
              control={control}
              rules={{ required: 'Academic year is required' }}
              render={({ field }) => (
                <input 
                  {...field} 
                  type="text" 
                  placeholder="2024-2025"
                  className={errors.academicYear ? styles.errorInput : ''}
                />
              )}
            />
            {errors.academicYear && <span className={styles.error}>{errors.academicYear.message}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Term *</label>
            <Controller
              name="term"
              control={control}
              rules={{ required: 'Term is required' }}
              render={({ field }) => (
                <select {...field} className={errors.term ? styles.errorInput : ''}>
                  <option value="Term 1">Term 1</option>
                  <option value="Term 2">Term 2</option>
                  <option value="Term 3">Term 3</option>
                  <option value="Semester 1">Semester 1</option>
                  <option value="Semester 2">Semester 2</option>
                </select>
              )}
            />
            {errors.term && <span className={styles.error}>{errors.term.message}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Periods Per Day *</label>
            <Controller
              name="periodsPerDay"
              control={control}
              rules={{ required: 'Periods per day is required', min: 1, max: 12 }}
              render={({ field }) => (
                <input 
                  {...field} 
                  type="number" 
                  min="1" 
                  max="12"
                  className={errors.periodsPerDay ? styles.errorInput : ''}
                />
              )}
            />
            {errors.periodsPerDay && <span className={styles.error}>{errors.periodsPerDay.message}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Days Per Week *</label>
            <Controller
              name="daysPerWeek"
              control={control}
              rules={{ required: 'Days per week is required', min: 1, max: 7 }}
              render={({ field }) => (
                <select {...field} className={errors.daysPerWeek ? styles.errorInput : ''}>
                  <option value="5">5 Days (Mon-Fri)</option>
                  <option value="6">6 Days (Mon-Sat)</option>
                  <option value="7">7 Days (Mon-Sun)</option>
                </select>
              )}
            />
            {errors.daysPerWeek && <span className={styles.error}>{errors.daysPerWeek.message}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Period Duration (minutes) *</label>
            <Controller
              name="periodDuration"
              control={control}
              rules={{ required: 'Period duration is required', min: 20, max: 90 }}
              render={({ field }) => (
                <input 
                  {...field} 
                  type="number" 
                  min="20" 
                  max="90"
                  className={errors.periodDuration ? styles.errorInput : ''}
                />
              )}
            />
            {errors.periodDuration && <span className={styles.error}>{errors.periodDuration.message}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>School Start Time *</label>
            <Controller
              name="schoolStartTime"
              control={control}
              rules={{ required: 'Start time is required' }}
              render={({ field }) => (
                <input 
                  {...field} 
                  type="time"
                  className={errors.schoolStartTime ? styles.errorInput : ''}
                />
              )}
            />
            {errors.schoolStartTime && <span className={styles.error}>{errors.schoolStartTime.message}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>School End Time *</label>
            <Controller
              name="schoolEndTime"
              control={control}
              rules={{ required: 'End time is required' }}
              render={({ field }) => (
                <input 
                  {...field} 
                  type="time"
                  className={errors.schoolEndTime ? styles.errorInput : ''}
                />
              )}
            />
            {errors.schoolEndTime && <span className={styles.error}>{errors.schoolEndTime.message}</span>}
          </div>

          <div className={styles.formGroup}>
            <label>Effective From *</label>
            <Controller
              name="effectiveFrom"
              control={control}
              rules={{ required: 'Effective date is required' }}
              render={({ field }) => (
                <input 
                  {...field} 
                  type="date"
                  className={errors.effectiveFrom ? styles.errorInput : ''}
                />
              )}
            />
            {errors.effectiveFrom && <span className={styles.error}>{errors.effectiveFrom.message}</span>}
          </div>
        </div>

        <div className={styles.breaksSection}>
          <div className={styles.breaksSectionHeader}>
            <h4>Break Times</h4>
            <button 
              type="button" 
              onClick={addBreak}
              className={styles.addBreakButton}
            >
              + Add Break
            </button>
          </div>

          {breakFields.map((field, index) => (
            <div key={field.id} className={styles.breakItem}>
              <div className={styles.breakItemHeader}>
                <h5>Break {index + 1}</h5>
                <button 
                  type="button"
                  onClick={() => removeBreak(index)}
                  className={styles.removeButton}
                >
                  Remove
                </button>
              </div>

              <div className={styles.breakGrid}>
                <div className={styles.formGroup}>
                  <label>Break Name *</label>
                  <Controller
                    name={`breaks.${index}.breakName`}
                    control={control}
                    rules={{ required: 'Break name is required' }}
                    render={({ field }) => (
                      <input {...field} type="text" placeholder="Lunch Break" />
                    )}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>After Period *</label>
                  <Controller
                    name={`breaks.${index}.afterPeriod`}
                    control={control}
                    rules={{ required: 'After period is required' }}
                    render={({ field }) => (
                      <select {...field}>
                        <option value="">Select Period</option>
                        {Array.from({ length: periodsPerDay }, (_, i) => (
                          <option key={i} value={i + 1}>After Period {i + 1}</option>
                        ))}
                      </select>
                    )}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Duration (minutes) *</label>
                  <Controller
                    name={`breaks.${index}.duration`}
                    control={control}
                    rules={{ required: 'Duration is required', min: 5, max: 60 }}
                    render={({ field }) => (
                      <input {...field} type="number" min="5" max="60" />
                    )}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Start Time *</label>
                  <Controller
                    name={`breaks.${index}.startTime`}
                    control={control}
                    rules={{ required: 'Start time is required' }}
                    render={({ field }) => (
                      <input {...field} type="time" />
                    )}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>End Time *</label>
                  <Controller
                    name={`breaks.${index}.endTime`}
                    control={control}
                    rules={{ required: 'End time is required' }}
                    render={({ field }) => (
                      <input {...field} type="time" />
                    )}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.formActions}>
          <button type="submit" className={styles.saveButton}>
            Save Configuration
          </button>
        </div>
      </form>
    </div>
  );
};

export default TimetableConfiguration;